/**
 * useNewHelp Hook
 *
 * Resolves whether contextual help exists for the current route against the
 * git-backed "new help" API. The document slug mirrors the route path, e.g.
 * the page at `/account/users` resolves to slug `account/users`; if that
 * doesn't exist it falls back to `account/index`.
 *
 * Availability is checked against a cached, portal-wide list of slugs (public,
 * no auth) so it resolves instantly on every page after the first load. The
 * actual document content is only fetched on demand via `getDocument()`.
 *
 * @example
 * const { available, loading, getDocument } = useNewHelp();
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { fetchNewHelpDocument, fetchNewHelpList, NewHelpDocument, resolveNewHelpSlug } from './newHelpManager';

export interface UseNewHelpReturn {
    /** Whether a help document is available for the current route. */
    available: boolean;
    /** Whether availability is still being resolved. */
    loading: boolean;
    /** Fetch the resolved document's content. Resolves null if none is available. */
    getDocument: () => Promise<NewHelpDocument | null>;
}

export const useNewHelp = (): UseNewHelpReturn => {
    const { pathname } = useLocation();
    const [slug, setSlug] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        setLoading(true);

        fetchNewHelpList().then((list) => {
            if (!cancelled) {
                setSlug(resolveNewHelpSlug(pathname, list));
                setLoading(false);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [pathname]);

    const getDocument = useCallback(() => {
        if (!slug) return Promise.resolve(null);

        return fetchNewHelpDocument(slug);
    }, [slug]);

    return { available: slug !== null, loading, getDocument };
};
